"use client"

import { useRef } from 'react'
import Link from 'next/link'
import { 
  Baby, 
  Syringe, 
  Brain, 
  Apple, 
  Thermometer,
  Stethoscope
} from 'lucide-react'
import { useIntersectionObserver } from '@/hooks/use-intersection-observer'
import { useScrollY } from '@/hooks/use-scroll-progress'

const services = [
  {
    icon: Baby,
    title: 'Breastfeeding Support',
    description: 'IBCLC-guided latch help, low milk worries, pumping plans, and weaning support.',
    href: '/breastfeeding',
    color: 'from-indigo-400 to-indigo-600',
    bgColor: 'bg-indigo-100',
    iconColor: 'text-indigo-500',
    hoverBg: 'group-hover:bg-indigo-500',
  }, 
  {
    icon: Syringe,
    title: 'Vaccinations',
    description: 'Routine immunizations as per standard pediatric schedules, with clear after-care guidance.',
    href: '/vaccinations',
    color: 'from-peach to-rose-400',
    bgColor: 'bg-peach/10',
    iconColor: 'text-peach',
    hoverBg: 'group-hover:bg-peach',
  },
  {
    icon: Brain,
    title: 'Development Support',
    description: 'Milestone screening and behavioral guidance, with next steps if any delay is suspected.',
    color: 'from-amber-400 to-orange-500',
    bgColor: 'bg-amber-100',
    iconColor: 'text-amber-500',
    hoverBg: 'group-hover:bg-amber-500',
  },
  {
    icon: Apple,
    title: 'Nutrition & Feeding',
    description: 'Feeding plans, picky eating support, and practical nutrition advice for healthy growth.',
    color: 'from-green-400 to-emerald-500',
    bgColor: 'bg-green-100',
    iconColor: 'text-green-500',
    hoverBg: 'group-hover:bg-green-500',
  },
  {
    icon: Stethoscope,
    title: 'Acute Illness Care',
    description: 'Fever, cough/cold, loose motions, rashes, breathing issues, and dehydration assessment.',
    color: 'from-blue-400 to-blue-600',
    bgColor: 'bg-blue-100',
    iconColor: 'text-blue-500',
    hoverBg: 'group-hover:bg-blue-500',
  },
  {
    icon: Thermometer,
    title: 'Newborn Checkups',
    description: 'Weight, feeding, jaundice checks, and practical newborn care guidance for the first weeks.',
    color: 'from-teal to-teal-dark',
    bgColor: 'bg-teal/10',
    iconColor: 'text-teal',
    hoverBg: 'group-hover:bg-teal',
  },
 
]


function ServiceCard({ 
  service, 
  index 
}: { 
  service: typeof services[0]
  index: number 
}) {
  const { ref: cardRef, isIntersecting: isInView } = useIntersectionObserver({ 
    threshold: 0.1, 
    once: true 
  })
  const Icon = service.icon

  const cardContent = (
    <div
      ref={cardRef}
      className={`service-card h-full relative bg-white rounded-3xl p-4 lg:p-6 overflow-hidden group card-hover hover:-translate-y-3 transition-all duration-300 ${service.href ? 'cursor-pointer' : 'cursor-default'} ${isInView ? 'reveal visible' : 'reveal'}`}
      style={{ 
        transitionDelay: `${index * 100}ms`
      }}
    >
      {/* Hover gradient background */}
      <div className={`absolute inset-0 bg-linear-to-br ${service.color} rounded-3xl -z-10 opacity-0 scale-90 group-hover:opacity-100 group-hover:scale-100 transition-all duration-400`} />

      {/* Decorative circles */}
      <div className={`absolute -top-10 -right-10 w-32 h-32 rounded-full bg-linear-to-br ${service.color} scale-0 opacity-0 group-hover:scale-150 group-hover:opacity-20 transition-all duration-500`} />
      <div className={`absolute -bottom-10 -left-10 w-24 h-24 rounded-full bg-linear-to-br ${service.color} scale-0 opacity-0 group-hover:scale-150 group-hover:opacity-20 transition-all duration-500 delay-100`} />

      {/* Content */}
      <div className="relative z-10 flex h-full flex-col">
        {/* Icon container */}
        <div className={`w-16 h-16 rounded-2xl ${service.bgColor} flex items-center justify-center mb-6 group-hover:bg-white/20 transition-all duration-300`}>
          <Icon className={`w-8 h-8 ${service.iconColor} group-hover:text-white transition-colors duration-300`} />
        </div>

        {/* Title */}
        <h3 className="font-semibold text-xl text-navy mb-3 group-hover:text-white transition-colors duration-300">
          {service.title}
        </h3>

        {/* Description */}
        <p className="text-muted-foreground leading-relaxed mb-6 group-hover:text-white/90 transition-colors duration-300 flex-1">
          {service.description}
        </p>

        {/* CTA */}
        {/* <motion.div 
          className="flex items-center gap-2 font-medium text-teal"
          variants={{
            hover: { color: "#ffffff", x: 5 }
          }}
          transition={{ duration: 0.3 }}
        >
          <span>Learn more</span>
          <motion.div
            animate={{ x: [0, 5, 0] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <ArrowRight className="w-4 h-4" />
          </motion.div>
        </motion.div> */}
      </div>

      {/* Shine effect */}
      <div className="absolute inset-0 bg-linear-to-r from-transparent via-white/20 to-transparent -translate-x-full pointer-events-none group-hover:translate-x-full transition-transform duration-700 ease-in-out" />
    </div>
  )

  if (service.href) {
    return (
      <Link href={service.href} aria-label={`Learn more about ${service.title}`} className="block h-full">
        {cardContent}
      </Link>
    )
  }

  return cardContent
}


export function ServicesSection() {
  const containerRef = useRef<HTMLDivElement>(null)
  const scrollY = useScrollY()
  const { ref: headerRef, isIntersecting: isHeaderInView } = useIntersectionObserver({ 
    threshold: 0.1, 
    once: true 
  })
  
  const backgroundOffset = scrollY * 0.2

  return (
    <section 
      id="services" 
      ref={containerRef}
      className="py-16 lg:py-32 bg-cream/50 relative overflow-hidden min-h-dvh w-full flex items-center"
    >
      {/* Animated background patterns */}
      <div 
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(91,180,165,0.1),transparent_50%)]"
        style={{ transform: `translateY(${backgroundOffset}px)` }}
      />
      <div 
        className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(255,181,167,0.1),transparent_50%)]"
        style={{ transform: `translateY(${backgroundOffset}px)` }}
      />
      
      {/* Floating elements */}
      <div
        className="absolute top-20 left-10 w-4 h-4 rounded-full bg-teal/30 hidden sm:block animate-float"
        style={{ animationDuration: '4s' }}
      />
      <div
        className="absolute bottom-32 right-20 w-6 h-6 rounded-full bg-peach/30 hidden sm:block animate-float"
        style={{ animationDuration: '5s', animationDelay: '1s' }}
      />
      <div
        className="absolute top-1/2 right-10 w-3 h-3 rounded-full bg-teal/20 animate-pulse-soft"
        style={{ animationDuration: '3s', animationDelay: '0.5s' }}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div 
          ref={headerRef}
          className={`text-center max-w-3xl mx-auto mb-12 lg:mb-16 ${isHeaderInView ? 'reveal visible' : 'reveal'}`}
        >
          <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-teal/10 text-teal text-sm font-medium mb-6 hover:scale-105 transition-transform duration-300 ${isHeaderInView ? 'scale-in' : ''}`}
            style={{ animationDelay: '200ms' }}
          >
            Our Services
          </div>
          
          <h2 className={`font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-6 text-balance ${isHeaderInView ? 'reveal visible' : 'reveal'}`}
            style={{ transitionDelay: '300ms' }}
          >
            Comprehensive Care for{' '}
            <span className="text-gradient">Every Stage</span>
          </h2>
          
          <p className={`text-lg text-muted-foreground leading-relaxed ${isHeaderInView ? 'reveal visible' : 'reveal'}`}
            style={{ transitionDelay: '400ms' }}
          >
            Practical, clinic-based care for newborns, children, and teens. From vaccinations and growth tracking
            to feeding guidance, development checks, and acute illness care.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
          {services.map((service, index) => (
            <ServiceCard 
              key={service.title} 
              service={service} 
              index={index} 
            />
          ))}
        </div>
      </div>
    </section>
  )
}
